import React, { useMemo, useState } from "react";
import { Box } from "@mui/material";
import { useTranslation } from "react-i18next";
import Role from "../../../enums/Role";
import { hasRoleAtLeast } from "../../../utils/authorization";
import UserStatus from "../../../enums/UserStatus";
import { HugeiconsIcon } from "@hugeicons/react";
import {
  ViewIcon,
  IncognitoIcon,
  CancelCircleIcon,
  Mail01Icon,
  RecoveryMailIcon,
  UserAdd01Icon,
  BankIcon,
} from "@hugeicons-pro/core-stroke-rounded";
import {
  GET_PLATFORM_USERS,
  GET_USER_BANK_ACCOUNT_STATUS,
} from "../../../graphql/user";
import { useApolloClient, useLazyQuery, useMutation } from "@apollo/client";
import useInfiniteScroll from "hooks/useInfiniteScroll";
import { DEFAULT_PAGINATION_SIZE } from "components/common/Constants";
import { useCheckUserRole } from "hooks/useCheckUserRole";
import { useImpersonation } from "contexts/ImpersonationContext";
import { START_IMPERSONATION_SESSION } from "graphql/impersonation";
import {
  RemoveUserModal,
  ResendUserInviteModal,
  ResetUserPasswordModal,
  SendUserBankAccountInviteModal,
} from "components/common/users/UserModules";
import { DataTable, toast } from "@outpave/ds";
import type { DataColumn, RowAction } from "@outpave/ds";
import { surnameFirstName } from "utils/fullName";
import UserAvatar from "components/common/users/UserAvatar";
import RenderUserStatus from "components/common/RenderUserStatus";
import type { FilterData } from "pages/platform/PlatformUsers";

const truncateStyle: React.CSSProperties = {
  display: "block",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};

interface User {
  id: string;
  userId: string;
  name: string;
  firstName?: string;
  lastName?: string | null;
  profilePicUrl?: string | null;
  userStatus: string;
  role: string;
  details?: string;
  email: string;
  accountName?: string;
  address?: unknown;
}

/** One node of the platform `users` connection, in the fields this table reads. */
interface PlatformUserNode {
  id: string;
  userId: string;
  email: string;
  firstName: string;
  lastName?: string | null;
  profilePicUrl?: string | null;
  status: string;
  role: string;
  address?: unknown;
  company?: { businessLegalName: string } | null;
}

interface PlatformUserConnection {
  edges: { cursor: string; node: PlatformUserNode }[];
}

interface UsersTableProps {
  handleOpenUserProfileDrawer: (
    userId: string,
    edit?: boolean,
    reactivateUser?: boolean,
  ) => void;
  filterData: FilterData;
  refreshUsers?: number;
  handleRefreshUsers: () => void;
  /** Off on one account's own page, where every row is that account's. */
  showAccount?: boolean;
}

const PlatformUsersTable: React.FC<UsersTableProps> = (props) => {
  const {
    filterData,
    handleOpenUserProfileDrawer,
    refreshUsers,
    handleRefreshUsers,
    showAccount = true,
  } = props;
  const { t } = useTranslation();

  const [openResendUserInviteModal, setOpenResendUserInviteModal] =
    useState<boolean>(false);
  const [openResetUserPasswordModal, setOpenResetUserPasswordModal] =
    useState<boolean>(false);
  const [openRemoveUserModal, setOpenRemoveUserModal] =
    useState<boolean>(false);
  const [openSendBankAccountInviteModal, setOpenSendBankAccountInviteModal] =
    useState<boolean>(false);
  // The user whose kebab was last opened. Used for the bank-account prefetch
  // context and to feed the modals below.
  const [actionTarget, setActionTarget] = useState<User | null>(null);

  const isPlatformRole = useCheckUserRole(Role.PlatformAdmin);
  const { startSession } = useImpersonation();
  const apolloClient = useApolloClient();
  const [startImpersonation, { loading: startingImpersonation }] = useMutation(
    START_IMPERSONATION_SESSION,
  );

  const defaultPaginationVariables = useMemo(() => {
    if (!filterData || Object.keys(filterData).length === 0) return {};
    return {
      first: DEFAULT_PAGINATION_SIZE,
      after: undefined,
      status: filterData.status?.length ? filterData.status : undefined,
      searchTerm: filterData.searchTerm,
      companyId:
        filterData.companyId === "showAll" ? undefined : filterData.companyId,
    };
  }, [filterData]);

  const extractUsers = (
    data: Record<string, PlatformUserConnection>,
  ): User[] => {
    const firstKey = Object.keys(data)[0];
    return (
      data[firstKey]?.edges?.map((item) => ({
        id: item.node.id,
        userId: item.node.userId,
        email: item.node.email,
        name: surnameFirstName(item.node),
        firstName: item.node.firstName,
        lastName: item.node.lastName,
        profilePicUrl: item.node.profilePicUrl,
        userStatus: item.node.status,
        role: item.node.role,
        address: item.node.address,
        accountName: item.node.company?.businessLegalName,
      })) ?? []
    );
  };

  const { dataArray, displayLoading, handleScroll, isInfiniteScrollFetching } =
    useInfiniteScroll({
      query: GET_PLATFORM_USERS,
      initialVariables: defaultPaginationVariables,
      extractData: extractUsers,
      refetchIndex: refreshUsers,
    });

  const [
    fetchBankAccountStatus,
    { data: bankAccountStatusData, loading: bankAccountStatusLoading },
  ] = useLazyQuery(GET_USER_BANK_ACCOUNT_STATUS, {
    fetchPolicy: "network-only",
  });

  // Bank-account status applies only to the user it was fetched for; any
  // other row reads `undefined`. The bank-invite action stays visible (via
  // `hidden`) but `disabled` until (a) the kebab is opened for that row and
  // (b) the lazy query resolves, so the item count doesn't shift mid-display.
  const bankStatusForUser = (id: string): boolean | undefined =>
    bankAccountStatusData?.userById?.id === id
      ? bankAccountStatusData?.userById?.hasPersonalBankAccount
      : undefined;

  const handleStartImpersonation = async (user: User) => {
    try {
      const { data } = await startImpersonation({
        variables: { targetUserId: user.id },
      });
      const session = data?.startImpersonationSession;
      if (!session) {
        toast.error(t("Failed to start impersonation session"));
        return;
      }
      startSession(session);
    } catch (err: unknown) {
      console.error("Failed to start impersonation", err);
      toast.error(
        (err instanceof Error && err.message) ||
          t("Failed to start impersonation session"),
      );
      return;
    }
    try {
      await apolloClient.clearStore();
    } finally {
      window.location.assign("/redirect");
    }
  };

  const handleActionsOpen = (user: User) => {
    setActionTarget(user);
    if (user.userStatus === UserStatus.ACTIVE) {
      fetchBankAccountStatus({ variables: { id: user.id } });
    }
  };

  /** DS actions take no row argument (the closure captures it), so `hidden`
   *  is a boolean here and this drops the hidden ones. */
  const visibleActions = (
    actions: (RowAction & { hidden?: boolean })[],
  ): RowAction[] =>
    actions
      .filter((action) => !action.hidden)
      .map(({ hidden, ...action }) => action);

  const rowActions = (user: User): RowAction[] =>
    visibleActions([
      {
        label: t("View User Info"),
        icon: (
          <HugeiconsIcon
            icon={ViewIcon}
            size={16}
            color="var(--color-text-tertiary)"
          />
        ),
        onClick: () => handleOpenUserProfileDrawer(user.id),
      },
      {
        label: startingImpersonation
          ? t("StartingImpersonation")
          : t("Impersonate User"),
        icon: (
          <HugeiconsIcon
            icon={IncognitoIcon}
            size={16}
            color="var(--color-text-tertiary)"
          />
        ),
        onClick: () => handleStartImpersonation(user),
        hidden:
          !isPlatformRole ||
          user.userStatus !== UserStatus.ACTIVE ||
          hasRoleAtLeast(user.role, Role.PlatformAdmin),
      },
      {
        label: t("Deactivate User"),
        icon: (
          <HugeiconsIcon
            icon={CancelCircleIcon}
            size={16}
            color="var(--color-text-tertiary)"
          />
        ),
        onClick: () => {
          setActionTarget(user);
          setOpenRemoveUserModal(true);
        },
        hidden: user.userStatus === UserStatus.INACTIVE,
      },
      {
        label: t("Resend User Invite"),
        icon: (
          <HugeiconsIcon
            icon={Mail01Icon}
            size={16}
            color="var(--color-text-tertiary)"
          />
        ),
        onClick: () => {
          setActionTarget(user);
          setOpenResendUserInviteModal(true);
        },
        hidden:
          user.userStatus !== UserStatus.PENDING ||
          user.role === Role.Cardholder,
      },
      {
        label: t("Reset User Password"),
        icon: (
          <HugeiconsIcon
            icon={RecoveryMailIcon}
            size={16}
            color="var(--color-text-tertiary)"
          />
        ),
        onClick: () => {
          setActionTarget(user);
          setOpenResetUserPasswordModal(true);
        },
        hidden: user.role === Role.Cardholder,
      },
      {
        label: t("Reactivate User"),
        icon: (
          <HugeiconsIcon
            icon={UserAdd01Icon}
            size={16}
            color="var(--color-text-tertiary)"
          />
        ),
        onClick: () => handleOpenUserProfileDrawer(user.id, true, true),
        hidden: user.userStatus !== UserStatus.INACTIVE,
      },
      {
        label: t("Send Personal Bank Account Invite"),
        icon: (
          <HugeiconsIcon
            icon={BankIcon}
            size={16}
            color="var(--color-text-tertiary)"
          />
        ),
        onClick: () => {
          setActionTarget(user);
          setOpenSendBankAccountInviteModal(true);
        },
        // `hidden` only checks attributes known up-front, so the item is
        // present from the instant the menu opens (no late-appearing item =>
        // no text shift). The async `hasBankAccount` check feeds `disabled`
        // instead, which morphs the item in place without changing the count.
        hidden:
          user.userStatus !== UserStatus.ACTIVE ||
          user.role === Role.Cardholder,
        disabled:
          bankAccountStatusLoading || bankStatusForUser(user.id) === true,
      },
    ]);

  const columns: DataColumn<User>[] = useMemo(
    () => [
      {
        id: "name",
        header: t("Name"),
        width: "20%",
        cell: (u) => ({
          avatar: <UserAvatar size="sm" user={u} />,
          content: u.name,
        }),
      },
      {
        id: "email",
        header: t("Email"),
        width: "30%",
        cell: (u) => (
          <span title={u.email} style={truncateStyle}>
            {u.email}
          </span>
        ),
      },
      {
        id: "userStatus",
        header: t("User Status"),
        width: "12%",
        cell: (u) => <RenderUserStatus userStatus={u.userStatus} />,
      },
      {
        id: "role",
        header: t("Role"),
        // Without the account column, role takes the width the account would have.
        ...(showAccount && { width: "10%" }),
        cell: (u) => t(u.role),
      },
      ...(showAccount
        ? [
            {
              id: "accountName",
              header: t("Account"),
              cell: (u: User) => (
                <span style={truncateStyle}>{u.accountName}</span>
              ),
            },
          ]
        : []),
    ],
    [t, showAccount],
  );

  return (
    <>
      <Box sx={{ position: "relative", height: "100%" }}>
        <DataTable<User>
          columns={columns}
          rows={dataArray as User[]}
          getRowKey={(u) => u.id}
          loading={displayLoading}
          isFetchingMore={isInfiniteScrollFetching}
          onRowClick={(u) => handleOpenUserProfileDrawer(u.id)}
          onScroll={handleScroll}
          emptyState={t("No users found")}
          rowActions={rowActions}
          rowActionsLabel={t("RowActions")}
          onRowActionsOpen={handleActionsOpen}
          loadingMoreText={t("LoadingMore")}
          stickyHeader
        />
      </Box>

      {actionTarget && (
        <>
          <RemoveUserModal
            openRemoveUserModal={openRemoveUserModal}
            setOpenRemoveUserModal={setOpenRemoveUserModal}
            userId={actionTarget.id}
            userEmail={actionTarget.email}
            handleUpdateUser={handleRefreshUsers}
          />

          <ResetUserPasswordModal
            openResetUserPasswordModal={openResetUserPasswordModal}
            setOpenResetUserPasswordModal={setOpenResetUserPasswordModal}
            userEmail={actionTarget.email}
          />

          <ResendUserInviteModal
            openResendUserInviteModal={openResendUserInviteModal}
            setOpenResendUserInviteModal={setOpenResendUserInviteModal}
            userEmail={actionTarget.email}
            handleUpdateUser={handleRefreshUsers}
          />

          <SendUserBankAccountInviteModal
            openSendBankAccountInviteModal={openSendBankAccountInviteModal}
            setOpenSendBankAccountInviteModal={
              setOpenSendBankAccountInviteModal
            }
            userId={actionTarget.id}
            userEmail={actionTarget.email}
          />
        </>
      )}
    </>
  );
};

export default PlatformUsersTable;
